import React, { useState, useEffect } from 'react';
import {
  Stack,
  Typography,
  Button,
  Box,
  IconButton,
  CircularProgress,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ImageIcon from '@mui/icons-material/Image';
import {
  attachmentMime,
  countImageAttachments,
  isImage,
  isLocalFileImage,
  isPdf,
} from './healthUtils';

function buildExistingPreviewItems(attachments) {
  const items = [];
  for (let i = 0; i < attachments.length; i += 1) {
    const a = attachments[i];
    const mime = attachmentMime(a);
    const url = a.url;
    if (!url) continue;
    items.push({ url, key: a.key, isImage: isImage(mime), mimeType: mime });
  }
  return items;
}

function buildPendingPreviewItems(pendingFiles, pendingPreviewUrls) {
  const items = [];
  for (let i = 0; i < pendingFiles.length; i += 1) {
    const f = pendingFiles[i];
    const url = pendingPreviewUrls[i];
    if (!url) continue;
    items.push({
      url,
      isImage: isLocalFileImage(f),
      mimeType: f.type,
    });
  }
  return items;
}

function openAttachmentPreview(attachment, allItems, onPreview) {
  const mime = attachmentMime(attachment);
  const url = attachment.url;
  if (!url) return;
  let index = 0;
  for (let i = 0; i < allItems.length; i += 1) {
    if (attachment.key && allItems[i].key === attachment.key) {
      index = i;
      break;
    }
  }
  onPreview({
    url,
    key: attachment.key,
    isImage: isImage(mime),
    mimeType: mime,
    items: allItems,
    index,
  });
}

export default function HealthAttachmentsSection({
  title,
  existingAttachments,
  pendingFiles,
  onPendingChange,
  onRemoveExisting,
  onPreview,
  request,
  disabled,
  onFilesPicked,
  healthFileId,
  onPdfCreated,
}) {
  const [pendingPreviewUrls, setPendingPreviewUrls] = useState([]);
  const [pdfCreating, setPdfCreating] = useState(false);

  useEffect(() => {
    const urls = [];
    for (let i = 0; i < pendingFiles.length; i += 1) {
      urls.push(URL.createObjectURL(pendingFiles[i]));
    }
    setPendingPreviewUrls(urls);
    return () => {
      for (let i = 0; i < urls.length; i += 1) {
        URL.revokeObjectURL(urls[i]);
      }
    };
  }, [pendingFiles]);

  function previewPending(file, url, allItems, index) {
    if (!url) return;
    onPreview({
      url,
      isImage: isLocalFileImage(file),
      mimeType: file.type,
      items: allItems,
      index,
    });
  }

  function handleFilePick(e) {
    const input = e.target;
    const picked = input.files;
    if (picked && picked.length > 0) {
      const next = [];
      for (let i = 0; i < picked.length; i += 1) {
        next.push(picked[i]);
      }
      onPendingChange([...pendingFiles, ...next]);
      if (onFilesPicked && next[0]) onFilesPicked(next[0]);
    }
    input.value = '';
  }

  const hasAttachments = existingAttachments.length > 0 || pendingFiles.length > 0;
  const galleryItems = [
    ...buildExistingPreviewItems(existingAttachments),
    ...buildPendingPreviewItems(pendingFiles, pendingPreviewUrls),
  ];
  const imageAttachmentCount = countImageAttachments(existingAttachments);

  async function createPdfFromImages() {
    if (!healthFileId || !request || pdfCreating || imageAttachmentCount === 0) return;
    setPdfCreating(true);
    try {
      const res = await request(`/api/health/files/${healthFileId}/pdf-from-images`, { method: 'POST' });
      if (res.ok && onPdfCreated) onPdfCreated(await res.json());
    } catch (e) {
      // ignore
    } finally {
      setPdfCreating(false);
    }
  }

  return (
    <Stack spacing={1}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1}>
        <Typography variant="subtitle2" color="text.secondary">
          {title}
        </Typography>
        {healthFileId && imageAttachmentCount > 0 && (
          <IconButton
            size="small"
            color="primary"
            onClick={createPdfFromImages}
            disabled={disabled || pdfCreating}
            title="Create PDF from images"
            aria-label="Create PDF from images"
          >
            {pdfCreating ? <CircularProgress size={18} /> : <PictureAsPdfIcon fontSize="small" />}
          </IconButton>
        )}
      </Stack>
      {hasAttachments && (
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          {existingAttachments.map((a, idx) => {
            const mime = attachmentMime(a);
            const label = a.filename || a.key || `Attachment ${idx + 1}`;
            return (
              <Box key={a.key || `existing-${idx}`} sx={{ position: 'relative', width: 72 }}>
                <Box
                  onClick={() => openAttachmentPreview(a, galleryItems, onPreview)}
                  role="button"
                  sx={{
                    width: 72,
                    height: 72,
                    borderRadius: 1,
                    overflow: 'hidden',
                    bgcolor: 'action.hover',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    border: 1,
                    borderColor: 'divider',
                  }}
                >
                  {isImage(mime) && a.url ? (
                    <img src={a.url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  ) : isPdf(mime) ? (
                    <PictureAsPdfIcon sx={{ fontSize: 32 }} />
                  ) : (
                    <ImageIcon sx={{ color: 'text.disabled' }} />
                  )}
                </Box>
                {onRemoveExisting && (
                  <IconButton
                    size="small"
                    onClick={() => onRemoveExisting(a.key)}
                    sx={{
                      position: 'absolute',
                      top: -4,
                      right: -4,
                      bgcolor: 'rgba(0,0,0,0.55)',
                      color: 'white',
                      width: 22,
                      height: 22,
                      '&:hover': { bgcolor: 'rgba(0,0,0,0.75)' },
                    }}
                    aria-label="Remove attachment"
                  >
                    <DeleteOutlineIcon sx={{ fontSize: 14 }} />
                  </IconButton>
                )}
                <Typography
                  variant="caption"
                  color="text.secondary"
                  noWrap
                  title={label}
                  sx={{ display: 'block', mt: 0.5, maxWidth: 72 }}
                >
                  {label}
                </Typography>
              </Box>
            );
          })}
          {pendingFiles.map((f, idx) => {
            const url = pendingPreviewUrls[idx];
            const thumbIsImage = isLocalFileImage(f);
            return (
              <Box key={`${f.name}-${idx}`} sx={{ position: 'relative', width: 72 }}>
                <Box
                  onClick={() => {
                    let index = 0;
                    for (let i = 0; i < galleryItems.length; i += 1) {
                      if (galleryItems[i].url === url) {
                        index = i;
                        break;
                      }
                    }
                    previewPending(f, url, galleryItems, index);
                  }}
                  role="button"
                  sx={{
                    width: 72,
                    height: 72,
                    borderRadius: 1,
                    overflow: 'hidden',
                    bgcolor: 'action.hover',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: url ? 'pointer' : 'default',
                    border: 1,
                    borderColor: 'divider',
                  }}
                >
                  {url && thumbIsImage ? (
                    <img src={url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  ) : url && isPdf(f.type) ? (
                    <PictureAsPdfIcon sx={{ fontSize: 32 }} />
                  ) : (
                    <ImageIcon sx={{ color: 'text.disabled' }} />
                  )}
                </Box>
                <IconButton
                  size="small"
                  onClick={() => onPendingChange(pendingFiles.filter((_, i) => i !== idx))}
                  sx={{
                    position: 'absolute',
                    top: -4,
                    right: -4,
                    bgcolor: 'rgba(0,0,0,0.55)',
                    color: 'white',
                    width: 22,
                    height: 22,
                    '&:hover': { bgcolor: 'rgba(0,0,0,0.75)' },
                  }}
                  aria-label="Remove file"
                >
                  <DeleteOutlineIcon sx={{ fontSize: 14 }} />
                </IconButton>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  noWrap
                  title={f.name}
                  sx={{ display: 'block', mt: 0.5, maxWidth: 72 }}
                >
                  {f.name}
                </Typography>
              </Box>
            );
          })}
        </Stack>
      )}
      <Button variant="outlined" component="label" size="small" startIcon={<AddIcon />} disabled={disabled}>
        Add attachments
        <input
          type="file"
          hidden
          multiple
          accept=".pdf,.docx,.jpg,.jpeg,.png,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,image/jpeg,image/png"
          onChange={handleFilePick}
        />
      </Button>
    </Stack>
  );
}
